import { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Check, ChevronLeft, ChevronRight, Folder, Loader2, Moon, Save, Search, Settings2, Sun, X, AlertTriangle } from "lucide-react";
import type { CustomMockup, MockupCollection, MockupFolder } from "@erc/shared";
import { DesignAreaEditor, type AreaRect } from "./DesignAreaEditor";
import type { useSupplierVariations } from "../../lib/useSupplierVariations";
import env from "../../env";

// Mockup grubu secici: TUM mockuplar duz listede (klasor bir filtre), secililer
// ustte buyuk ve siralanabilir; alan belirleme ve marka/model/renk atamasi
// pencereden cikmadan yapilir. Alani olmayan mockup da gorunur (uyarili):
// secilebilir ama alan belirlenene kadar uzerine tasarim basilmaz.

const API_BASE = env.API_URL || "";
const urlOf = (m: CustomMockup) => { const u = m.thumbnailUrl || m.imageUrl; return u.startsWith("http") ? u : `${API_BASE}${u}`; };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const areasOf = (m: CustomMockup): any[] => (Array.isArray((m as any).designAreas) ? (m as any).designAreas : []);
const hasArea = (m: CustomMockup) => areasOf(m).length > 0 || (m.designAreaW > 20 && m.designAreaH > 20);
const areaCount = (m: CustomMockup) => areasOf(m).length || (hasArea(m) ? 1 : 0);
const themeOf = (m: CustomMockup): "light" | "dark" => ((areasOf(m)[0]?.theme as string | undefined) ?? m.theme) === "dark" ? "dark" : "light";

type AreaFilter = "all" | "has" | "none";
type ThemeFilter = "all" | "light" | "dark";

export function CollectionMockupSelector({
  collection, allMockups, folders, onSave, onCancel, onSaveAreas, vars, onAssignVariation, noun = "Mockup",
}: {
  collection: MockupCollection;
  allMockups: CustomMockup[];
  folders: MockupFolder[];
  onSave: (mockupIds: string[]) => void;
  onCancel: () => void;
  onSaveAreas: (mockupId: string, areas: AreaRect[]) => Promise<CustomMockup | null>;
  vars?: ReturnType<typeof useSupplierVariations>;
  onAssignVariation?: (id: string, patch: { brand?: string | null; model?: string | null; color?: string | null }) => void;
  noun?: string;
}) {
  const [order, setOrder] = useState<string[]>(collection.mockups.map((m) => m.id));
  const selected = new Set(order);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<CustomMockup | null>(null);

  // ---- Filtreler ----
  const [q, setQ] = useState("");
  const [area, setArea] = useState<AreaFilter>("all");
  const [theme, setTheme] = useState<ThemeFilter>("all");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [folder, setFolder] = useState<string>(""); // "" tumu, "root" klasorsuz, id (alt klasorler dahil)
  const [onlySelected, setOnlySelected] = useState(false);

  const folderOptions = useMemo(() => {
    const byParent: Record<string, MockupFolder[]> = {};
    folders.forEach((f) => { (byParent[f.parentId ?? "root"] ||= []).push(f); });
    const out: { id: string; label: string }[] = [];
    const walk = (p: string, d: number) => (byParent[p] || []).forEach((f) => { out.push({ id: f.id, label: `${"— ".repeat(d)}${f.name}` }); walk(f.id, d + 1); });
    walk("root", 0);
    return out;
  }, [folders]);
  const folderSet = useMemo(() => {
    if (!folder || folder === "root") return null;
    const s = new Set<string>([folder]);
    let grew = true;
    while (grew) { grew = false; for (const f of folders) if (f.parentId && s.has(f.parentId) && !s.has(f.id)) { s.add(f.id); grew = true; } }
    return s;
  }, [folder, folders]);

  // Marka/model secenekleri: katalog + mockuplarda kayitli degerler
  const brands = useMemo(() => [...new Set([...(vars?.brands ?? []), ...allMockups.map((m) => m.brand).filter(Boolean) as string[]])].sort(), [vars, allMockups]);
  const models = useMemo(() => [...new Set(allMockups.filter((m) => !brand || m.brand === brand).map((m) => m.model).filter(Boolean) as string[])].sort(), [allMockups, brand]);
  const colors = useMemo(() => [...new Set(allMockups.filter((m) => (!brand || m.brand === brand) && (!model || m.model === model)).map((m) => m.color).filter(Boolean) as string[])].sort(), [allMockups, brand, model]);

  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const filtered = useMemo(() => {
    const nq = norm(q);
    return allMockups.filter((m) => {
      if (onlySelected && !selected.has(m.id)) return false;
      if (area === "has" && !hasArea(m)) return false;
      if (area === "none" && hasArea(m)) return false;
      if (theme !== "all" && themeOf(m) !== theme) return false;
      if (brand && m.brand !== brand) return false;
      if (model && m.model !== model) return false;
      if (color && m.color !== color) return false;
      if (folder === "root" && m.folderId) return false;
      if (folderSet && !(m.folderId && folderSet.has(m.folderId))) return false;
      if (nq && !norm([m.label, m.brand, m.model, m.color].filter(Boolean).join(" ")).includes(nq)) return false;
      return true;
    });
  }, [allMockups, q, area, theme, brand, model, color, folder, folderSet, onlySelected, order]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => ({ has: allMockups.filter(hasArea).length, none: allMockups.filter((m) => !hasArea(m)).length }), [allMockups]);
  const selectedMockups = order.map((id) => allMockups.find((m) => m.id === id)).filter((m): m is CustomMockup => Boolean(m));
  const selectedNoArea = selectedMockups.filter((m) => !hasArea(m)).length;
  const activeFilters = [q, area !== "all", theme !== "all", brand, model, color, folder, onlySelected].filter(Boolean).length;

  const toggle = (id: string) => setOrder((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const move = (i: number, d: -1 | 1) => setOrder((p) => { const n = [...p]; const j = i + d; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const resetFilters = () => { setQ(""); setArea("all"); setTheme("all"); setBrand(""); setModel(""); setColor(""); setFolder(""); setOnlySelected(false); };

  const varSelects = (m: CustomMockup, compact = false) => onAssignVariation && (
    <div className={clsx("grid grid-cols-3 gap-1", compact ? "mt-1" : "mt-1.5")} onClick={(e) => e.stopPropagation()}>
      <select value={m.brand || ""} onChange={(e) => onAssignVariation(m.id, { brand: e.target.value || null, model: null, color: null })} title="Marka"
        className="min-w-0 rounded border border-gray-200 bg-white px-1 py-0.5 text-[10px] text-gray-600">
        <option value="">Marka</option>{brands.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
      <select value={m.model || ""} onChange={(e) => onAssignVariation(m.id, { model: e.target.value || null, color: null })} title="Model"
        className="min-w-0 rounded border border-gray-200 bg-white px-1 py-0.5 text-[10px] text-gray-600">
        <option value="">Model</option>
        {[...new Set([...(vars?.modelsForBrand(m.brand || "") ?? []), ...(m.model ? [m.model] : [])])].map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <select value={m.color || ""} onChange={(e) => onAssignVariation(m.id, { color: e.target.value || null })} title="Renk"
        className="min-w-0 rounded border border-gray-200 bg-white px-1 py-0.5 text-[10px] text-gray-600">
        <option value="">Renk</option>
        {[...new Set([...(vars?.colorsFor(m.brand || "", m.model || "") ?? []), ...(m.color ? [m.color] : [])])].map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );

  const badges = (m: CustomMockup) => (
    <div className="absolute left-1.5 top-1.5 flex gap-1">
      {hasArea(m)
        ? <span className="rounded-full border border-emerald-200 bg-emerald-50/95 px-1.5 text-[10px] font-medium text-emerald-700">{areaCount(m)} alan</span>
        : <span className="rounded-full border border-amber-300 bg-amber-50/95 px-1.5 text-[10px] font-medium text-amber-700">Alan yok</span>}
      <span className={clsx("flex items-center rounded-full border px-1 text-[10px]", themeOf(m) === "dark" ? "border-gray-600 bg-gray-700 text-gray-100" : "border-gray-200 bg-white/95 text-gray-600")}>
        {themeOf(m) === "dark" ? <Moon className="h-2.5 w-2.5" /> : <Sun className="h-2.5 w-2.5" />}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-2xl">
        {/* Baslik */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{noun} Seç — {collection.name}</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {order.length} seçili · sıra = Etsy yükleme sırası
              {selectedNoArea > 0 && <span className="ml-2 text-amber-700">· {selectedNoArea} seçilinin alanı yok (tasarım basılmaz)</span>}
            </p>
          </div>
          <button onClick={onCancel} className="rounded-lg p-2 hover:bg-gray-100"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {/* Secililer — buyuk */}
          {selectedMockups.length > 0 && (
            <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Seçili ({selectedMockups.length}) — okla sırala, ⚙ alan belirle</span>
                <button onClick={() => setOrder([])} className="text-[11px] font-medium text-red-500 hover:text-red-700">Tümünü kaldır</button>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {selectedMockups.map((m, i) => (
                  <div key={m.id} className={clsx("group relative overflow-hidden rounded-xl border-2 bg-white", hasArea(m) ? "border-indigo-300" : "border-amber-300")}>
                    <div className={clsx("relative flex items-center justify-center", themeOf(m) === "dark" ? "bg-gray-800" : "bg-gray-50")} style={{ height: 170 }}>
                      <img src={urlOf(m)} alt={m.label} className="max-h-full max-w-full object-contain" crossOrigin="anonymous" />
                      <span className="absolute bottom-1.5 left-1.5 flex h-6 min-w-6 items-center justify-center rounded-md bg-indigo-600 px-1.5 text-xs font-bold text-white">{i + 1}</span>
                      <button onClick={() => toggle(m.id)} title="Kaldır"
                        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800/70 text-white hover:bg-red-500"><X className="h-3.5 w-3.5" /></button>
                      <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                        <button onClick={() => setEditing(m)} title="Alanı düzenle"
                          className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white/95 text-indigo-600 hover:bg-indigo-50"><Settings2 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => move(i, -1)} disabled={i === 0} title="Öne al"
                          className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white/95 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                        <button onClick={() => move(i, 1)} disabled={i === selectedMockups.length - 1} title="Sona al"
                          className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white/95 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                      </div>
                      {badges(m)}
                    </div>
                    <div className="px-2 pb-2 pt-1">
                      <p className="truncate text-xs font-medium text-gray-800" title={m.label}>{m.label}</p>
                      {varSelects(m, true)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filtre cubugu */}
          <div className="sticky top-0 z-10 -mx-5 mb-3 border-b border-gray-100 bg-white/95 px-5 py-2 backdrop-blur">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: ad, marka, model, renk"
                  className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>
              <div className="flex overflow-hidden rounded-lg border border-gray-200 text-xs">
                {([["all", `Tümü ${allMockups.length}`], ["has", `Alanı olan ${counts.has}`], ["none", `Alanı olmayan ${counts.none}`]] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setArea(k)} className={clsx("px-2.5 py-1.5", area === k ? (k === "none" ? "bg-amber-500 text-white" : "bg-gray-900 text-white") : "hover:bg-gray-50")}>{l}</button>
                ))}
              </div>
              <div className="flex overflow-hidden rounded-lg border border-gray-200 text-xs">
                {([["all", "Açık+Koyu"], ["light", "Açık"], ["dark", "Koyu"]] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setTheme(k)} className={clsx("px-2.5 py-1.5", theme === k ? "bg-gray-900 text-white" : "hover:bg-gray-50")}>{l}</button>
                ))}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <select value={folder} onChange={(e) => setFolder(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5">
                <option value="">Tüm klasörler</option>
                <option value="root">Klasörsüz</option>
                {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <select value={brand} onChange={(e) => { setBrand(e.target.value); setModel(""); setColor(""); }} className="rounded-lg border border-gray-200 px-2 py-1.5">
                <option value="">Marka: tümü</option>{brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={model} onChange={(e) => { setModel(e.target.value); setColor(""); }} className="rounded-lg border border-gray-200 px-2 py-1.5">
                <option value="">Model: tümü</option>{models.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <select value={color} onChange={(e) => setColor(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5">
                <option value="">Renk: tümü</option>{colors.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <label className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1.5">
                <input type="checkbox" checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)} /> Yalnız seçililer
              </label>
              <span className="ml-auto text-gray-500">{filtered.length} sonuç</span>
              {activeFilters > 0 && <button onClick={resetFilters} className="text-indigo-600 hover:underline">Filtreleri temizle</button>}
            </div>
          </div>

          {/* Tum mockuplar */}
          {filtered.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">Filtreye uyan {noun.toLowerCase()} yok.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {filtered.map((m) => {
                const isSel = selected.has(m.id);
                return (
                  <div key={m.id} className={clsx("group relative overflow-hidden rounded-xl border-2 bg-white transition-all",
                    isSel ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 hover:border-gray-300")}>
                    <button onClick={() => toggle(m.id)} className={clsx("relative flex w-full items-center justify-center", themeOf(m) === "dark" ? "bg-gray-800" : "bg-gray-100")} style={{ height: 120 }}>
                      <img src={urlOf(m)} alt={m.label} loading="lazy" className="max-h-full max-w-full object-contain" crossOrigin="anonymous" />
                      {badges(m)}
                      {isSel && (
                        <span className="absolute right-1.5 top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1 text-[10px] font-bold text-white">
                          {order.indexOf(m.id) + 1}
                        </span>
                      )}
                      {!isSel && <span className="absolute right-1.5 top-1.5 hidden h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-white/80 group-hover:flex"><Check className="h-3 w-3 text-gray-400" /></span>}
                    </button>
                    <div className="px-1.5 pb-1.5 pt-1">
                      <div className="flex items-center gap-1">
                        <p className="min-w-0 flex-1 truncate text-[11px] text-gray-700" title={m.label}>{m.label}</p>
                        <button onClick={() => setEditing(m)} title="Alanı düzenle"
                          className={clsx("flex h-5 w-5 shrink-0 items-center justify-center rounded border", hasArea(m) ? "border-gray-200 text-indigo-600 hover:bg-indigo-50" : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100")}>
                          {hasArea(m) ? <Settings2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                        </button>
                      </div>
                      {varSelects(m, true)}
                      {m.folderId && <p className="mt-0.5 flex items-center gap-0.5 truncate text-[9px] text-gray-400"><Folder className="h-2.5 w-2.5" />{folders.find((f) => f.id === m.folderId)?.name}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
          <span className="mr-auto text-xs text-gray-400">Marka/model/renk ve alan değişiklikleri anında kaydedilir; seçim "Kaydet" ile.</span>
          <button onClick={onCancel} className="rounded-lg px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-100">İptal</button>
          <button onClick={() => { setSaving(true); onSave(order); }} disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
          </button>
        </div>
      </div>

      {editing && (
        <DesignAreaEditor
          mockup={editing}
          onSave={async (areas) => { await onSaveAreas(editing.id, areas); setEditing(null); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}
